import httpClient from '../../../../api/httpClient';
import API_CONFIG from '../../../../config/api.config';
import { 
  Employee, 
  EmployeeFormData, 
  EmployeeListResponse 
} from '../types/employee.types';

const ENDPOINT = API_CONFIG.ENDPOINTS.EMPLOYEES || '/api/employees';

export const employeeApi = {
  /**
   * Get paginated list of employees with optional filters
   */
  getAll: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
    employeeType?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }): Promise<EmployeeListResponse> => {
    // Convert page/limit to skip/take for the backend
    const skip = params?.page && params?.limit ? (params.page - 1) * params.limit : 0;
    const take = params?.limit || 10;
    
    // Build the query parameters
    const queryParams: any = {
      skip,
      take,
    };
    
    // Add filters if they exist
    if (params?.search) queryParams.search = params.search;
    if (params?.status) queryParams.status = params.status;
    if (params?.employeeType) queryParams.employeeType = params.employeeType;
    if (params?.sortBy) queryParams.sortBy = params.sortBy;
    if (params?.sortOrder) queryParams.sortOrder = params.sortOrder;
    
    const response = await httpClient.get<Employee[]>(ENDPOINT, { params: queryParams });
    
    // Transform the response to match EmployeeListResponse
    return {
      data: response.data,
      total: response.data.length, // The backend should return the total count
      page: params?.page || 1,
      limit: take,
      totalPages: Math.ceil(response.data.length / take)
    };
  },

  /**
   * Get a single employee by ID
   */
  getById: async (id: string): Promise<Employee> => {
    const response = await httpClient.get<Employee>(`${ENDPOINT}/${id}`);
    return response.data;
  },

  /**
   * Create a new employee
   */
  create: async (data: EmployeeFormData): Promise<Employee> => {
    const response = await httpClient.post<Employee>(ENDPOINT, data);
    return response.data;
  },

  /**
   * Update an employee
   */
  update: async (id: string, data: Partial<EmployeeFormData>): Promise<Employee> => {
    const response = await httpClient.patch<Employee>(`${ENDPOINT}/${id}`, data);
    return response.data;
  },

  /**
   * Delete an employee
   */
  delete: async (id: string): Promise<void> => {
    await httpClient.delete(`${ENDPOINT}/${id}`);
  },

  /**
   * Update employee status
   */
  updateStatus: async (
    id: string, 
    status: 'ACTIVE' | 'INACTIVE' | 'ON_LEAVE' | 'TERMINATED'
  ): Promise<Employee> => {
    const response = await httpClient.patch<Employee>(
      `${ENDPOINT}/${id}/status`, 
      null, 
      { params: { status } }
    );
    return response.data;
  },

  /**
   * Search employees by query
   */
  search: async (query: string): Promise<Employee[]> => {
    const response = await httpClient.get<Employee[]>(`${ENDPOINT}/search`, {
      params: { q: query }
    });
    return response.data;
  }
};
